import React, { useEffect, useState, forwardRef, useImperativeHandle, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { API_CONFIG, getApiUrl, apiFetch } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { downloadCsv } from '../../utils/exportCsv';
import { isAdmin as checkAdmin } from '../../utils/roleUtils';
import './Invoices.css';
import { formatAppDate, parseApiDate } from '../../utils/dateUtils';

const InvoicesList = forwardRef(({ onSelectInvoice, selectedInvoiceId, onNew }, ref) => {
  const { t } = useTranslation('sales');
  const { user } = useAuth();
  const isAdmin = checkAdmin(user);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');

  useImperativeHandle(ref, () => ({
    fetchInvoices
  }));

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      if (statusFilter !== 'all') {
        queryParams.append('status', statusFilter);
      }
      if (paymentStatusFilter !== 'all') {
        queryParams.append('payment_status', paymentStatusFilter);
      }

      const response = await apiFetch(getApiUrl(`/api/invoices/?${queryParams.toString()}`));

      if (response.ok) {
        const data = await response.json();
        setInvoices(data || []);
        setError(null);
      } else {
        setError(t('invoices.list.error.fetchFailed'));
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setError(t('invoices.list.error.loadingError'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, paymentStatusFilter]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = (
      invoice.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.po_number?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const matchesStatus = statusFilter === 'all' || invoice.status === statusFilter;
    const matchesPaymentStatus = paymentStatusFilter === 'all' || invoice.payment_status === paymentStatusFilter;

    return matchesSearch && matchesStatus && matchesPaymentStatus;
  });

  const getStatusText = (status, type = 'status') => {
    if (type === 'payment_status') {
      switch (status) {
        case 'PAID': return t('invoices.view.paymentStatus.paid');
        case 'PARTIAL': return t('invoices.view.paymentStatus.partial');
        case 'UNPAID': return t('invoices.view.paymentStatus.unpaid');
        case 'REFUNDED': return t('invoices.view.paymentStatus.refunded');
        case 'OVERDUE': return t('invoices.view.paymentStatus.overdue');
        default: return t('invoices.view.paymentStatus.unpaid');
      }
    } else {
      switch (status) {
        case 'PAID': return t('invoices.view.status.paid');
        case 'SENT': return t('invoices.view.status.sent');
        case 'DRAFT': return t('invoices.view.status.draft');
        case 'VIEWED': return t('invoices.view.status.viewed');
        case 'OVERDUE': return t('invoices.view.status.overdue');
        case 'PARTIAL': return t('invoices.view.status.partial');
        case 'CANCELLED': return t('invoices.view.status.cancelled');
        case 'VOID': return t('invoices.view.status.void');
        default: return t('invoices.view.status.draft');
      }
    }
  };

  const getStatusBadge = (status, type = 'status') => {
    if (type === 'payment_status') {
      const statusClass =
        status === 'PAID' ? 'status-completed' :
        status === 'PARTIAL' ? 'status-warning' :
        status === 'UNPAID' ? 'status-pending' :
        status === 'REFUNDED' ? 'status-inactive' : 'status-pending';
      return <span className={`status-badge ${statusClass}`}>{getStatusText(status || 'UNPAID', type)}</span>;
    } else {
      const statusClass =
        status === 'PAID' ? 'status-completed' :
        status === 'SENT' ? 'status-active' :
        status === 'DRAFT' ? 'status-pending' :
        status === 'OVERDUE' ? 'status-danger' :
        status === 'PARTIAL' ? 'status-warning' :
        status === 'CANCELLED' ? 'status-cancelled' :
        status === 'VOID' ? 'status-inactive' : 'status-pending';
      return <span className={`status-badge ${statusClass}`}>{getStatusText(status || 'DRAFT', type)}</span>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('invoices.view.defaultValues.notAvailable');
    return formatAppDate(dateString);
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return `$${parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getDaysOverdue = (dueDate, status) => {
    if (!dueDate || status === 'PAID') return null;

    const due = parseApiDate(dueDate);
    const today = new Date();
    const diffTime = today - due;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays > 0 ? diffDays : null;
  };

  const handleNewClick = () => {
    if (onNew) {
      onNew();
    }
  };

  if (loading && invoices.length === 0) {
    return <div className="list-container"><div className="loading">{t('invoices.list.loading')}</div></div>;
  }

  return (
    <div className="list-container">
      <div className="list-header">
        <h2>{t('invoices.list.title')}</h2>
        <div className="header-actions">
          {isAdmin && (
            <button
              className="btn-secondary"
              onClick={() => downloadCsv('invoices', { status: statusFilter })}
              title={t('common:actions.export', 'Export CSV')}
            >
              CSV
            </button>
          )}
          <button className="btn-primary" onClick={handleNewClick}>
            <i className="icon">+</i> {t('invoices.toolbar.buttons.newInvoice')}
          </button>
        </div>
      </div>

      <div className="list-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder={t('invoices.list.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t('invoices.list.filters.allStatus')}</option>
            <option value="DRAFT">{t('invoices.view.status.draft')}</option>
            <option value="SENT">{t('invoices.view.status.sent')}</option>
            <option value="VIEWED">{t('invoices.view.status.viewed')}</option>
            <option value="PARTIAL">{t('invoices.view.status.partial')}</option>
            <option value="PAID">{t('invoices.view.status.paid')}</option>
            <option value="OVERDUE">{t('invoices.view.status.overdue')}</option>
            <option value="CANCELLED">{t('invoices.view.status.cancelled')}</option>
            <option value="VOID">{t('invoices.view.status.void')}</option>
          </select>
          <select
            value={paymentStatusFilter}
            onChange={(e) => setPaymentStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t('invoices.list.filters.allPaymentStatus')}</option>
            <option value="UNPAID">{t('invoices.view.paymentStatus.unpaid')}</option>
            <option value="PARTIAL">{t('invoices.view.paymentStatus.partial')}</option>
            <option value="PAID">{t('invoices.view.paymentStatus.paid')}</option>
            <option value="REFUNDED">{t('invoices.view.paymentStatus.refunded')}</option>
          </select>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="list-table">
        <table>
          <thead>
            <tr>
              <th>{t('invoices.list.headers.invoiceNumber')}</th>
              <th>{t('invoices.list.headers.customer')}</th>
              <th>{t('invoices.view.fields.totalAmount')}</th>
              <th>{t('invoices.view.fields.amountDue')}</th>
              <th>{t('invoices.list.headers.invoiceDate')}</th>
              <th>{t('invoices.view.fields.status')}</th>
              <th>{t('invoices.list.headers.paymentStatus')}</th>
              <th>{t('invoices.list.headers.overdue')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredInvoices.length === 0 ? (
              <tr>
                <td colSpan="8" className="no-data">
                  {searchTerm ? t('invoices.list.noData.noResults') : t('invoices.list.noData.noInvoices')}
                </td>
              </tr>
            ) : (
              filteredInvoices.map(invoice => (
                <tr
                  key={invoice.invoice_id}
                  onClick={() => onSelectInvoice(invoice.invoice_id)}
                  className={selectedInvoiceId === invoice.invoice_id ? 'selected' : ''}
                >
                  <td className="invoice-number">
                    <code>{invoice.invoice_number || t('invoices.view.defaultValues.notAvailable')}</code>
                  </td>
                  <td className="customer-name">
                    <strong>{invoice.customer_name || t('invoices.view.defaultValues.notAvailable')}</strong>
                  </td>
                  <td className="text-right">
                    {formatCurrency(invoice.total_amount)}
                  </td>
                  <td className="text-right">
                    {formatCurrency(invoice.amount_due)}
                  </td>
                  <td>
                    {formatDate(invoice.invoice_date)}
                  </td>
                  <td>
                    {getStatusBadge(invoice.status)}
                  </td>
                  <td>
                    {getStatusBadge(invoice.payment_status, 'payment_status')}
                  </td>
                  <td className="overdue-column">
                    {getDaysOverdue(invoice.due_date, invoice.status) && (
                      <span className="overdue-badge">
                        {getDaysOverdue(invoice.due_date, invoice.status)} {getDaysOverdue(invoice.due_date, invoice.status) === 1 ? 'day' : 'days'}
                      </span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="list-footer">
        <div className="record-count">
          {t('invoices.list.footer.recordCount', { filtered: filteredInvoices.length, total: invoices.length })}
        </div>
      </div>
    </div>
  );
});

InvoicesList.displayName = 'InvoicesList';

export default InvoicesList;